import React from "react";
import { Mail, User, ArrowRight } from "lucide-react";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { AuthErrorBanner } from "./AuthErrorBanner";

interface SignupFormProps {
  firstName: string;
  onFirstNameChange: (value: string) => void;
  lastName: string;
  onLastNameChange: (value: string) => void;
  email: string;
  onEmailChange: (value: string) => void;
  acceptedTerms: boolean;
  onAcceptedTermsChange: (value: boolean) => void;
  emailMarketingConsent: boolean;
  onEmailMarketingConsentChange: (value: boolean) => void;
  onSubmit: (e: React.FormEvent) => void;
  isLoading: boolean;
  error: string | null;
  /** Rendered inside the error banner — the "sign in instead" hand-off. */
  errorAction?: React.ReactNode;
  /** Rendered at the foot of the card — the standing link across to login. */
  footer?: React.ReactNode;
}

const inputClass =
  "block w-full pl-10 pr-3 py-2.5 border border-input rounded-lg text-sm text-foreground placeholder-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors bg-background/50";

export function SignupForm({
  firstName,
  onFirstNameChange,
  lastName,
  onLastNameChange,
  email,
  onEmailChange,
  acceptedTerms,
  onAcceptedTermsChange,
  emailMarketingConsent,
  onEmailMarketingConsentChange,
  onSubmit,
  isLoading,
  error,
  errorAction,
  footer,
}: SignupFormProps) {
  return (
    <div className="w-full max-w-md space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold text-foreground tracking-tight">
          Create your account
        </h1>
        <p className="text-sm text-muted-foreground">
          We'll email you a code to confirm it's you
        </p>
      </div>

      {error && <AuthErrorBanner message={error} action={errorAction} />}

      <div className="bg-white rounded-2xl shadow-lg border border-border/50 p-6 sm:p-8 space-y-6">
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="firstName"
                className="block text-sm font-medium text-foreground mb-1.5"
              >
                First Name
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-4 w-4 text-muted-foreground" />
                </div>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  autoComplete="given-name"
                  required
                  maxLength={100}
                  className={inputClass}
                  placeholder="Jane"
                  value={firstName}
                  onChange={(e) => onFirstNameChange(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="lastName"
                className="block text-sm font-medium text-foreground mb-1.5"
              >
                Last Name
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-4 w-4 text-muted-foreground" />
                </div>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  autoComplete="family-name"
                  required
                  maxLength={100}
                  className={inputClass}
                  placeholder="Doe"
                  value={lastName}
                  onChange={(e) => onLastNameChange(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-foreground mb-1.5"
            >
              Email
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-4 w-4 text-muted-foreground" />
              </div>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className={inputClass}
                placeholder="you@example.com"
                value={email}
                onChange={(e) => onEmailChange(e.target.value)}
              />
            </div>
          </div>

          {/* Both answers are recorded once, here, and never asked again. */}
          <div className="space-y-3 pt-1">
            <label
              htmlFor="acceptedTerms"
              className="flex items-start gap-3 text-sm cursor-pointer"
            >
              <input
                id="acceptedTerms"
                name="acceptedTerms"
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => onAcceptedTermsChange(e.target.checked)}
                className="mt-0.5 size-4 shrink-0 rounded border-primary text-primary accent-primary focus:ring-primary"
              />
              <span className="text-foreground">
                I accept the{" "}
                <a
                  href={import.meta.env.VITE_TERMS_OF_SERVICE}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-primary hover:underline"
                >
                  terms and conditions
                </a>
              </span>
            </label>

            <label
              htmlFor="emailMarketingConsent"
              className="flex items-start gap-3 text-sm cursor-pointer"
            >
              <input
                id="emailMarketingConsent"
                name="emailMarketingConsent"
                type="checkbox"
                checked={emailMarketingConsent}
                onChange={(e) =>
                  onEmailMarketingConsentChange(e.target.checked)
                }
                className="mt-0.5 size-4 shrink-0 rounded border-primary text-primary accent-primary focus:ring-primary"
              />
              <span className="text-foreground">
                I want to receive email updates about Apptly Health
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={isLoading || !acceptedTerms}
            className="w-full mt-6 group flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-gradient-primary from-navy to-royal-blue hover:from-navy/90 hover:to-royal-blue/90 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg"
            aria-busy={isLoading}
          >
            {isLoading ? (
              <>
                <LoadingSpinner className="h-4 w-4" />
                <span>Creating Account...</span>
              </>
            ) : (
              <>
                <span>Create Account</span>
                <ArrowRight
                  className="w-4 h-4 group-hover:translate-x-0.5 transition-transform"
                  aria-hidden="true"
                />
              </>
            )}
          </button>
        </form>

        {footer}
      </div>
    </div>
  );
}
